import styled from '@emotion/styled';
import { tokens } from '@hotjar/hotsauce';
import type { QuestionWithAnswers, QuestionAnswer } from './domain/QuestionAnswerMapper';

export interface ResponseAnswerModalProps {
  questionsWithAnswers: Record<string, QuestionWithAnswers>;
}

const QuestionContainer = styled.div`
  margin-bottom: ${tokens.spacer.x3};
`;

const QuestionText = styled.div`
  ${tokens.text('subtitle2')};
  margin-bottom: ${tokens.spacer.x0};
  color: ${tokens.color.text_default};
  box-sizing: border-box;
`;

interface OrderedAnswerListProps {
  hasSingleAnswer: boolean;
}

const OrderedAnswerList = styled.ol<OrderedAnswerListProps>`
  list-style: lower-roman;
  margin: 0;

  ${({ hasSingleAnswer }) =>
    hasSingleAnswer &&
    `
    list-style: none;
    padding: 0;
  `}

  li {
    margin-bottom: ${tokens.spacer.x1};
    padding: 0;
  }
`;

const AnswerText = styled.span`
  ${tokens.text('body2')};
  white-space: pre-line;
`;

const Quote = styled.strong`
  vertical-align: middle;
`;

const CommentText = styled.span`
  white-space: pre-line;
`;

const CommentWrapper = styled.span`
  margin-top: ${tokens.spacer.x0};
  ${tokens.text('body2')};
  color: ${tokens.color.text_light};
  display: block;
`;

type CommentProps = {
  text: string;
};

function Comment(props: CommentProps) {
  const { text } = props;
  return (
    <CommentWrapper>
      <Quote>&ldquo;&nbsp;</Quote>
      <CommentText>{text}</CommentText>
      <Quote>&nbsp;&rdquo;</Quote>
    </CommentWrapper>
  );
}

type AnswerListProps = {
  answers: QuestionAnswer[];
};

function AnswerList({ answers }: AnswerListProps) {
  return (
    <OrderedAnswerList hasSingleAnswer={answers.length === 1}>
      {answers.map((answer, index) => (
        // eslint-disable-next-line react/no-array-index-key
        <li key={index}>
          <AnswerText>{answer.answer}</AnswerText>
          {answer.comment && <Comment text={answer.comment} />}
        </li>
      ))}
    </OrderedAnswerList>
  );
}

export function ModalContent({ questionsWithAnswers }: ResponseAnswerModalProps): JSX.Element {
  return (
    <>
      {questionsWithAnswers &&
        Object.entries(questionsWithAnswers).map(([id, question]) => (
          <QuestionContainer key={id}>
            <QuestionText>{question.text}</QuestionText>
            {question.answers && <AnswerList answers={question.answers} />}
          </QuestionContainer>
        ))}
    </>
  );
}
